import cx from 'classnames';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useDispatch, useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {FileDropZone} from '~/common/components/FileDropZone/FileDropZone';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {usePostPhoto} from '~/ViewInvitation/hooks/usePostPhoto';
import {
  CLEAR_PENDING_PHOTOS,
  MARK_HOST_PHOTOS_PROCESSING,
  NAME_HOST_GALLERY,
  TOGGLE_HOST_GALLERY_EDITOR_OPEN,
  UPDATE_HOST_UPLOAD_PROGRESS,
} from '~/ViewInvitation/reducers/constants';
import {PhotoGallery} from '~/ViewInvitation/routes/Photos/PhotoGallery/PhotoGallery';
import {PhotoGalleryEditor} from '~/ViewInvitation/routes/Photos/PhotoGalleryEditor/PhotoGalleryEditor';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {PhotosEmptyState} from '~/ViewInvitation/routes/Photos/PhotosEmptyState/PhotosEmptyState';
import {PhotosModuleTile} from '~/ViewInvitation/routes/Photos/PhotosModuleTile/PhotosModuleTile';
import {PhotoViewer} from '~/ViewInvitation/routes/Photos/PhotoViewer/PhotoViewer';
import {
  selectHostGalleryName,
  selectHostUploadProcessing,
  selectHostUploadProgress,
  selectIsHostGalleryEditorOpen,
} from '~/ViewInvitation/selectors/photos';
import {isFullPreview, isHostOrCF, isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargeAndUp, useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ProgressAlert} from '~library/molecules/ProgressAlert';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {tanzanite} from '~libSass/base/_exports.sass';

import 'react-loading-skeleton/dist/skeleton.css';
import './HostPhotosModule.sass';

export const HOST_PHOTO_MODULE_PER_PAGE = 5;
const MAX_PHOTO_LENGTH = 5;
const COLLECTION = 'hostPhotos';

const loadingState = (
  <>
    <Skeleton className="photos__loading-skeleton-featured" />
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
  </>
);

export const HostPhotosModule = memo(
  ({preview = isPreview(), readonly = true, disableContent = false, editBtnRef}) => {
    const dispatch = useDispatch();
    const {getThumbnailUrl} = useImageProxySizer();
    const editable = preview && !readonly;
    const isPreviewAsGuest = isFullPreview();
    const eventId = useSelector((state) => state.eventDetails?.eventId);
    const stagedPhotos = useSelector((state) => state.photos.hostPhotos);
    const localGalleryName = useSelector(selectHostGalleryName);
    const [isPosting, setIsPosting] = useState(false);
    const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
    const storeGalleryEditorOpen = useSelector(selectIsHostGalleryEditorOpen);
    const [galleryEditorOpen, setGalleryEditorOpen] = useState(false);
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [photoViewerId, setPhotoViewerId] = useState(null);
    const hostUploadProgress = useSelector(selectHostUploadProgress);
    const hostUploadProcessing = useSelector(selectHostUploadProcessing);
    const [showProgress, setShowProgress] = useState(true);
    const [galleryFocusTrapPaused, setGalleryFocusTrapPaused] = useState(false);
    const [hasUploadedNoTitle, setHasUploadedNoTitle] = useState(false);
    const isLargeAndUp = useIsLargeAndUp();
    const isDesktop = useIsLargishAndUp();

    const fileUploadRef = useRef();

    const {
      photos,
      title: galleryName,
      isLoading: isLoadingPhotos,
    } = usePhotos({
      eventId,
      isHostModule: true,
      perPage: HOST_PHOTO_MODULE_PER_PAGE,
    });

    const closeGalleryEditor = useCallback(() => {
      dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: false});
    }, [dispatch]);

    const {doPostPhotos, doFeatureOnly} = usePostPhoto({
      setIsPosting,
      isPosting,
      closeGalleryEditor,
      isHostModule: true,
      galleryName,
      isCreationFlow: preview,
    });

    const isLoading = isLoadingPhotos || isPosting;
    const adjustedLength = isLoading ? MAX_PHOTO_LENGTH : Math.min(photos.length, MAX_PHOTO_LENGTH);
    const galleryNameFallback =
      isHostOrCF() && !photos.length && !hasUploadedNoTitle ? 'Your gallery name' : '';
    const galleryNameText = galleryName || galleryNameFallback;
    const emptyStateText = 'Psst... guests won’t see this gallery until you add photos!';

    const onViewGallery = useCallback(() => {
      setPhotoGalleryOpen(true);
    }, []);

    const onEditGallery = useCallback(() => {
      if (galleryName && !localGalleryName) {
        dispatch({type: NAME_HOST_GALLERY, payload: galleryName});
      }
      dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
      setPhotoGalleryOpen(false);
      setShowProgress(true);
    }, [dispatch, galleryName, localGalleryName]);

    const onViewPhotoFn = useCallback(
      (photoId) => () => {
        setPhotoViewerId(photoId);
        setPhotoViewerOpen(true);
        setGalleryFocusTrapPaused(true);
      },
      []
    );

    const onCloseGalleryEditor = useCallback(() => {
      dispatch({type: CLEAR_PENDING_PHOTOS});
      dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: false});
    }, [dispatch]);

    const onClosePhotoViewer = useCallback(() => {
      setGalleryFocusTrapPaused(false);
      setPhotoViewerOpen(false);
    }, []);

    const handlePost = useCallback(() => {
      setHasUploadedNoTitle(true);
      dispatch({
        type: MARK_HOST_PHOTOS_PROCESSING,
        payload: true,
      });
      doPostPhotos();
    }, [dispatch, doPostPhotos]);

    useEffect(() => {
      const successCount = stagedPhotos.filter((photo) => photo.status === 'success').length;
      const totalCount = stagedPhotos.length;
      const uploadPercent = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

      dispatch({
        type: UPDATE_HOST_UPLOAD_PROGRESS,
        payload: uploadPercent,
      });

      if (totalCount === 0) {
        dispatch({
          type: MARK_HOST_PHOTOS_PROCESSING,
          payload: false,
        });
      }
    }, [dispatch, stagedPhotos]);

    useEffect(() => {
      setGalleryEditorOpen(storeGalleryEditorOpen);
    }, [storeGalleryEditorOpen]);

    const doFeature = useCallback(
      async (photo, isStaged) => {
        const existingFeatured = photos.find((p) => p.position === 1);
        const updates = [];

        // A saved photo is already featured, unfeature it
        if (existingFeatured) {
          updates.push({...existingFeatured, position: null});
        }

        // The new featured photo is already saved, update it
        if (!isStaged) {
          updates.push({
            ...photo,
            position: 1,
          });
        }
        await doFeatureOnly(updates);
      },
      [photos, doFeatureOnly]
    );

    const showEditButton = isHostOrCF() && !editable && photos?.length > 0 && !isPreviewAsGuest;

    return (
      <>
        {isDesktop ? (
          <EditButton
            data-qa-id="edit-host-photos"
            ref={editBtnRef}
            hide={!editable}
            key="edit-host-photos" // Ensures re-generation when switching from desktop to MW
          />
        ) : (
          <EditButton
            data-qa-id="remove-host-photos"
            ref={editBtnRef}
            hide={!editable}
            text="Remove"
            key="remove-host-photos" // Ensures re-generation when switching from desktop to MW
          />
        )}
        <div className="host-photos__module-content">
          <div className="host-photos__module-header">
            <div className="host-photos__module-header__label">
              <Typography variant="label2" color="pebble" data-qa-id="host-photos-module-label">
                Gallery
              </Typography>
              {showEditButton && (
                <Button
                  variant="link"
                  icon={<EditLineIcon ratio={0.5} color={tanzanite} />}
                  data-qa-id="edit-host-photos-button"
                  ariaLabel="Open host gallery editor"
                  size="small"
                  semibold
                  onClick={onEditGallery}
                >
                  Edit
                </Button>
              )}
            </div>
            {galleryNameText && (
              <Typography variant="header6" data-qa-id="host-photos-module-title" semibold>
                {galleryNameText}
              </Typography>
            )}
          </div>
          {photos?.length === 0 && !isLoading ? (
            <FileDropZone
              show={!isPreviewAsGuest && isLargeAndUp}
              inputRef={fileUploadRef}
              disabled={isPreviewAsGuest}
            >
              <PhotosEmptyState
                showAddButton={!isPreviewAsGuest && !disableContent}
                isCreationFlow={preview}
                emptyStateText={emptyStateText}
                collection={COLLECTION}
                fileUploadRef={fileUploadRef}
                isHostModule
                fullWidth
              />
            </FileDropZone>
          ) : (
            <div className={`photos__carousel-host-${adjustedLength}`} data-qa-id="photos-carousel">
              {isLoading
                ? loadingState
                : photos
                    .slice(0, 5)
                    .map((photo, i) => (
                      <PhotosModuleTile
                        key={photo.id}
                        idx={i}
                        id={photo.photo_id}
                        url={photo.url}
                        width={photo.width}
                        height={photo.height}
                        length={photos.length}
                        isHostModule
                        getThumbnailUrl={getThumbnailUrl}
                        onViewGallery={onViewGallery}
                        onViewPhotoFn={onViewPhotoFn}
                        isViewAll={i === 4}
                      />
                    ))}
            </div>
          )}
          <PhotoGalleryEditor
            isOpen={galleryEditorOpen}
            onClose={onCloseGalleryEditor}
            stagedPhotos={stagedPhotos}
            isHostModule
            handlePost={handlePost}
            doFeature={doFeature}
            collection={COLLECTION}
            onViewPhotoFn={onViewPhotoFn}
            isPosting={isPosting}
            setPhotoGalleryOpen={setPhotoGalleryOpen}
            onEditGallery={onEditGallery}
            isCreationFlow={preview}
            setHasUploadedNoTitle={setHasUploadedNoTitle}
          />
          {photoGalleryOpen && (
            <PhotoGallery
              isOpen={photoGalleryOpen}
              onClose={() => setPhotoGalleryOpen(false)}
              isHostModule
              galleryName={galleryName}
              onEditGallery={onEditGallery}
              collection={COLLECTION}
              setPhotoGalleryOpen={setPhotoGalleryOpen}
              onViewPhotoFn={onViewPhotoFn}
              focusTrapPaused={galleryFocusTrapPaused}
              isPreviewAsGuest={isPreviewAsGuest}
            />
          )}
          {photoViewerOpen && (
            <PhotoViewer initialPhotoId={photoViewerId} onClose={onClosePhotoViewer} isHostModule />
          )}
          {showProgress && hostUploadProcessing && (
            <ProgressAlert
              className={cx({'host-upload__progress-alert-CF': preview})}
              progress={hostUploadProgress}
              label="Uploading photos..."
              closeText="Dismiss"
              onClose={() => setShowProgress(false)}
              width={21}
            />
          )}
          <PhotoGalleryUploadInput
            fileUploadRef={fileUploadRef}
            collection={COLLECTION}
            setPhotoGalleryOpen={setPhotoGalleryOpen}
            onEditGallery={onEditGallery}
          />
        </div>
      </>
    );
  }
);

HostPhotosModule.displayName = 'HostPhotosModule';
